// The decorative half of a styled city centre: per-style landmarks that grow
// with tier (colonnade + dome, stave towers, torii) and the team-coloured
// banners, whose shapes come from a lookup table keyed by the style's `banner`.

import {
  type Ctx,
  type Roofed,
  TEAM_FOLD,
  TEAM_LIT,
  type V,
  WALL_Y,
  box,
  disc,
  shell,
  slab,
} from './buildings_styled_kit.js';
import type { CentreStyleId } from './buildings_styled_kit.js';
import { BUILDING_STYLES, type BannerShape } from './styles.js';

// --- Landmarks: per-style extras that grow with tier ------------------------

type LandmarkBuilder = (c: Ctx, roof: Roofed) => V[];

export const LANDMARKS: Readonly<Record<CentreStyleId, LandmarkBuilder>> = {
  // Columns on the front face; a dome from tier 2; pediment + bigger dome at tier 3.
  imperial: (c, roof) => {
    const { B } = c.spec;
    const out: V[] = [];
    const cols = c.tier === 1 ? [-2, 2] : c.tier === 2 ? [-3, -1, 1, 3] : [-4, -2, 0, 2, 4];
    for (const x of cols) out.push(...box(x, 1, B, 1, c.spec.wallH + 1, 1, c.p.wall));
    if (c.tier === 3) {
      // Pediment over the colonnade.
      for (let i = 0; i < 5; i++)
        out.push(
          ...box(
            -(4 - i),
            roof.topY - 2 + i,
            B,
            2 * (4 - i) + 1,
            1,
            1,
            i === 0 ? c.p.trim : c.p.wallAlt,
          ),
        );
    }
    if (c.tier === 3) {
      // Showpiece skyline: two slim campanile towers behind the dome, gold-capped.
      for (const sx of [-1, 1]) {
        const x0 = sx < 0 ? -B : B - 1;
        out.push(...box(x0, 1, -B, 2, 9, 2, c.p.wall), ...box(x0, 10, -B, 2, 1, 2, c.p.trim));
        out.push(...box(x0, 11, -B, 2, 1, 2, c.p.roof), { x: x0, y: 12, z: -B, color: c.p.accent });
      }
    }
    if (c.tier >= 2) {
      const r = c.tier === 3 ? 3 : 2;
      let y = roof.topY + 1;
      for (let rr = r; rr >= 1; rr--, y++) out.push(...disc(rr, y, rr === r ? c.p.trim : c.p.wall));
      out.push({ x: 0, y, z: 0, color: c.p.accent });
    }
    return out;
  },
  // Stacked porch finials grow into a stave tower and a rune stone.
  nordic: (c) => {
    const out: V[] = [];
    if (c.tier >= 2) {
      // Row of round shields along the front eaves.
      for (let x = -c.spec.wallHalf + 1; x <= c.spec.wallHalf - 1; x += 2)
        out.push({ x, y: WALL_Y + c.spec.wallH - 1, z: c.spec.wallHalf + 1, color: c.p.trim });
    }
    if (c.tier === 3) {
      // Stave tower: three narrowing storeys, each with its own little roof.
      const cx = -3;
      const cz = -3;
      let y = WALL_Y;
      for (let i = 0; i < 3; i++) {
        const half = i === 2 ? 0 : 1;
        for (const v of shell(half, y, 2, () => c.p.wall))
          out.push({ ...v, x: v.x + cx, z: v.z + cz });
        y += 2;
        for (const v of slab(half + 1, y, c.p.roof)) out.push({ ...v, x: v.x + cx, z: v.z + cz });
        y++;
      }
      out.push(
        { x: cx, y, z: cz, color: c.p.accent },
        { x: cx, y: y + 1, z: cz, color: c.p.accent },
      );
      // Second, shorter stave tower.
      for (let i = 0; i < 2; i++) {
        const cx2 = 3;
        const cz2 = -3;
        const y2 = WALL_Y + i * 2;
        for (const v of shell(i === 1 ? 0 : 1, y2, 2, () => c.p.wall))
          out.push({ ...v, x: v.x + cx2, z: v.z + cz2 });
        for (const v of slab((i === 1 ? 0 : 1) + 1, y2 + 2, c.p.roof))
          out.push({ ...v, x: v.x + cx2, z: v.z + cz2 });
      }
      // Rune stone beside the entrance.
      out.push(...box(4, 1, 5, 1, 3, 1, c.p.base), { x: 4, y: 3, z: 5, color: c.p.accent });
    }
    return out;
  },
  // Lantern posts at the gate; a torii from tier 3.
  eastern: (c, roof) => {
    const { B } = c.spec;
    const out: V[] = [];
    for (const x of [-B + 1, B - 1]) {
      out.push(...box(x, 1, B, 1, 2, 1, c.p.trim), { x, y: 3, z: B, color: c.p.accent });
    }
    if (c.tier === 3) {
      out.push(...box(-2, 1, B, 1, 4, 1, c.p.trim), ...box(2, 1, B, 1, 4, 1, c.p.trim));
      out.push(...box(-3, 5, B, 7, 1, 1, c.p.trim), ...box(-2, 4, B, 5, 1, 1, c.p.door));
      // Golden spire on the pagoda and paired stone lanterns flanking the gate.
      for (let dy = 1; dy <= 2; dy++)
        out.push({ x: 0, y: roof.topY + dy, z: 0, color: c.p.accent });
      for (const x of [-4, 4])
        out.push(...box(x, 1, B - 1, 1, 2, 1, c.p.base), { x, y: 3, z: B - 1, color: c.p.roof });
    }
    return out;
  },
};

// --- Banners: team-coloured cloth on a pole ---------------------------------

type BannerBuilder = (x: number, z: number, y0: number, h: number, dir: 1 | -1, c: Ctx) => V[];

const pole = (x: number, z: number, y0: number, h: number, color: string): V[] =>
  box(x, y0, z, 1, h, 1, color);

const BANNERS: Readonly<Record<BannerShape, BannerBuilder>> = {
  // Hanging cloth under a crossbar, gold fringe, gold finial.
  vexillum: (x, z, y0, h, dir, c) => [
    ...pole(x, z, y0, h, c.p.trim),
    { x, y: y0 + h, z, color: c.p.accent },
    ...box(dir > 0 ? x + 1 : x - 2, y0 + h - 3, z, 2, 3, 1, TEAM_LIT, true),
    ...box(dir > 0 ? x + 1 : x - 2, y0 + h - 4, z, 2, 1, 1, c.p.accent),
  ],
  // Tapering pennant streaming from a tall mast.
  'pennant-mast': (x, z, y0, h, dir, c) => [
    ...pole(x, z, y0, h, c.p.door),
    { x, y: y0 + h, z, color: c.p.accent },
    ...box(dir > 0 ? x + 1 : x - 3, y0 + h - 1, z, 3, 1, 1, TEAM_LIT, true),
    ...box(dir > 0 ? x + 1 : x - 2, y0 + h - 2, z, 2, 1, 1, TEAM_FOLD, true),
    ...box(dir > 0 ? x + 1 : x - 1, y0 + h - 3, z, 1, 1, 1, TEAM_FOLD, true),
  ],
  // Tall narrow nobori flag riding the pole.
  nobori: (x, z, y0, h, dir, c) => [
    ...pole(x, z, y0, h, c.p.door),
    ...box(x, y0 + h, z, 1, 1, 1, c.p.accent),
    ...box(dir > 0 ? x + 1 : x - 1, y0 + h - 5, z, 1, 5, 1, TEAM_LIT, true),
    ...box(dir > 0 ? x + 1 : x - 1, y0 + h - 6, z, 1, 1, 1, c.p.trim),
  ],
  shared: (x, z, y0, h, dir) => [
    ...pole(x, z, y0, h, '#654321'),
    ...box(dir > 0 ? x + 1 : x - 2, y0 + h - 2, z, 2, 2, 1, TEAM_LIT, true),
  ],
};

/** Banner anchors per tier: roof apex for the village, front corners above that for larger tiers. */
export function banners(c: Ctx, roof: Roofed): V[] {
  const make = BANNERS[BUILDING_STYLES[c.style].banner];
  const { B } = c.spec;
  if (c.tier === 1) return make(0, 0, roof.topY + 1, 3, 1, c);
  const y0 = WALL_Y + c.spec.wallH + 1;
  const h = c.tier === 2 ? 4 : 6;
  return [...make(-B, B, y0, h, 1, c), ...make(B, B, y0, h, -1, c)];
}
